import { z } from 'zod';

// Each member is `.strict()` so that a foreign key forces the union to fall
// through to the matching member. Without it, `z.unknown()` makes `eq` optional
// and the non-strict object strips unrecognized keys, so every non-`eq`
// operator (`in`, `nin`, `gt`, ...) collapses to the first member as `{}`.
export const ResourceTriggerComparisonOperator = z.union([
  z.object({ eq: z.unknown() }).strict(),
  z.object({ ne: z.unknown() }).strict(),
  z.object({ gt: z.unknown() }).strict(),
  z.object({ gte: z.unknown() }).strict(),
  z.object({ lt: z.unknown() }).strict(),
  z.object({ lte: z.unknown() }).strict(),
  z.object({ in: z.array(z.unknown()) }).strict(),
  z.object({ nin: z.array(z.unknown()) }).strict(),
  z.object({ exists: z.boolean() }).strict(),
  z.object({ contains: z.string() }).strict(),
]);
export type ResourceTriggerComparisonOperator = z.infer<typeof ResourceTriggerComparisonOperator>;

// `.strict()` so foreign keys can't be silently stripped into an empty
// transition when a malformed operator falls through the union.
const ResourceTriggerTransitionOperandSchema = z
  .object({
    from: z.unknown().optional(),
    to: z.unknown().optional(),
  })
  .strict();

// Reject an empty operand (`{}`) at the union-value level, not inside the
// transition branch. `z.unknown()` in a v4 `z.object` makes the property
// optional regardless of `.strict()`, so `ResourceTriggerComparisonOperator`'s
// first `.strict()` branch also accepts `{}` (returning `{}`) and would
// short-circuit any transition-level refine. An empty operand never matches
// on the backend.
export const ResourceTriggerDiffCriterion = z.record(
  z.string(),
  z
    .union([ResourceTriggerComparisonOperator, ResourceTriggerTransitionOperandSchema])
    .refine((operand) => Object.keys(operand).length > 0, {
      message: 'Diff criterion operand must have at least one key',
    })
);
export type ResourceTriggerDiffCriterion = z.infer<typeof ResourceTriggerDiffCriterion>;

export const ResourceTriggerRelationshipCriterion = z.record(
  z.string(),
  z.object({
    where: z.record(z.string(), ResourceTriggerComparisonOperator),
  })
);
export type ResourceTriggerRelationshipCriterion = z.infer<
  typeof ResourceTriggerRelationshipCriterion
>;

const ResourceTriggerLeafCriteria = z
  .object({
    diff: ResourceTriggerDiffCriterion.optional(),
    relationships: ResourceTriggerRelationshipCriterion.optional(),
  })
  .strict()
  .refine((data) => data.diff ?? data.relationships ?? Object.keys(data).length === 0, {
    message:
      "Criteria must be an empty object {} (match all) or contain at least one of 'diff' or 'relationships'",
  });
type ResourceTriggerLeafCriteriaType = z.infer<typeof ResourceTriggerLeafCriteria>;

export type ResourceTriggerCriteria =
  | { and: ResourceTriggerCriteria[] }
  | { or: ResourceTriggerCriteria[] }
  | { not: ResourceTriggerCriteria }
  | ResourceTriggerLeafCriteriaType;

export const ResourceTriggerCriteria: z.ZodType<ResourceTriggerCriteria> = z.lazy(() =>
  z.union([
    z.object({ and: z.array(ResourceTriggerCriteria) }).strict(),
    z.object({ or: z.array(ResourceTriggerCriteria) }).strict(),
    z.object({ not: ResourceTriggerCriteria }).strict(),
    ResourceTriggerLeafCriteria,
  ])
);
